import React from 'react';
import { Box, Chip, Divider } from '@mui/material';
import * as Yup from 'yup';
import { useResizeObserver, useScrollableView } from '../../Layout';
import { useAddMessagingToDoMutation, useMarkMessagesAsReadMutation, useUserMessagesQuery } from '@uplift/queries';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { Color } from '../types';
import { MessageBubble, MessageItem, MessagingBubbleDirection } from './MessageBubble.component';
import { Loader } from '../../Loader';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { Message } from '@uplift/api';
import { useMessagingContext } from '../MessagingProvider.component';
import { debounce } from 'lodash';
import { CancelButton, FormModal, SubmitButton } from '../../Form';
import { ModalActions, ModalContent, ModalTitle } from '../../Modal';
import { Formik } from 'formik';
import { TextInput } from '../../Inputs';
import { useAlert } from '../../AlertProvider';


export type MessagesContainerProps = {
  conversationId: string;
  color: Color;
};


const AddToDoModal: React.VFC<{ message: Message; onClose: () => void }> = (props) => {
  const me = useCurrentuser();
  const alert = useAlert();
  const addMessagingToDoMutation = useAddMessagingToDoMutation();

  return (
    <Formik
      enableReinitialize
      initialValues={{
        messageId: props.message.id,
        description: '',
      }}
      onSubmit={async (values) => {
        await addMessagingToDoMutation.mutateAsync({ messageId: props.message.id, description: values.description });
        alert.success('To-Do added successfully');
        props.onClose();
      }}
      validationSchema={Yup.object().shape({
        messageId: Yup.string().required(),
        description: Yup.string().required('Description is required'),
      })}
    >
      <FormModal open onClose={() => props.onClose()} maxWidth="md" fullWidth>
        <ModalTitle>Add To-Do</ModalTitle>
        <ModalContent>
          <TextInput name="description" placeholder="Description" color="secondary" multiline minRows={3} />
          <Box pt={2} display="flex">
            <MessageBubble
              message={props.message}
              direction={me.id === props.message.senderId ? 'send' : 'receive'}
              color="secondary"
            />
          </Box>
        </ModalContent>
        <ModalActions>
          <CancelButton onClick={props.onClose}>Close</CancelButton>
          <SubmitButton color="secondary">Add</SubmitButton>
        </ModalActions>
      </FormModal>
    </Formik>
  );
};


const MessagesListItem: React.FC<{
  message: Message;
  direction: MessagingBubbleDirection;
  color: Color;
  markAsRead?: boolean;
  downloadableAttachments?: boolean;
  setAddToDoModal: (message: Message) => void;
}> = ({ markAsRead = true, downloadableAttachments = true, ...props }) => {
  // const me = useCurrentuser();
  const markAsReadMutation = useMarkMessagesAsReadMutation();

  const handleMarkAsRead = React.useMemo(() => debounce(() => {
    if (markAsRead) {
      markAsReadMutation.mutate({ messageId: props.message.id });
    }
  }, 1000), [ props.message.id, markAsRead ]);
  const handleMarkAsReadCancel = () => handleMarkAsRead.cancel();

  // Call the cancel function when the component unmounts
  React.useEffect(() => () => handleMarkAsReadCancel(), []);

  return (
    <MessageItem
      message={props.message}
      downloadableAttachments={downloadableAttachments}
      direction={props.direction}
      color={props.color}
      onAppearOnScreen={() => {
        if (props.message.seenAt || props.direction === 'send') return;
        handleMarkAsRead();
      }}
      onDisappearOnScreen={() => {
        handleMarkAsReadCancel();
      }}
      // actions={me.role === UserRole.provider ? (
      //   <MessageBubbleActionItem onClick={() => props.setAddToDoModal(props.message)}>
      //     Mark as To Do
      //   </MessageBubbleActionItem>
      // ) : undefined}
    />
  );
};


const MessagesGroup: React.FC<{
  color: Color;
  group: { date: string; messages: Message[] };
  setAddToDoModal: (message: Message) => void;
}> = ({ color, group, setAddToDoModal }) => {
  const me = useCurrentuser();
  const rootRef = React.useRef<HTMLDivElement>(null);

  const groupLabel = React.useMemo(() => {
    const date = CalendarDateService.date(group.date, DateFormats.ISO_DATE);

    if (date.isSame(CalendarDateService.date(), 'date')) {
      return 'Today';
    }

    return CalendarDateService.format(date, 'dddd, MMMM D', 'dddd, MMMM D, YYYY');
  }, [ group.date ]);

  return (
    <Box
      ref={rootRef}
      position="relative"
      data-testid="messages-group"
    >
      <Box sx={{
        position: 'absolute',
        width: '100%',
        top: 0,
        height: '51px',
        bgcolor: group.messages[0]?.seenAt ? 'transparent' : 'grey.50',
      }}
      >
        <Divider sx={{
          position: 'absolute',
          top: '25px',
          width: '100%',
        }}
        />
      </Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '51px',
          zIndex: 1,
        }}
      >
        <Chip
          sx={{
            bgcolor: 'white',
            cursor: 'pointer',

            '&.MuiChip-root:hover': {
              bgcolor: 'white',
            },
          }}
          variant="outlined"
          label={groupLabel}
          data-testid="messages-group-title"
          onClick={() => {
            rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      </Box>
      {group.messages.map((message) => (
        <MessagesListItem
          key={message.id}
          message={message}
          color={color}
          direction={message.senderId === me.id ? 'send' : 'receive'}
          setAddToDoModal={setAddToDoModal}
        />
      ))}
    </Box>
  );
};


export const MessagesContainer: React.FC<MessagesContainerProps> = ({ conversationId, color }) => {
  const me = useCurrentuser();
  const messagingContext = useMessagingContext();
  const scrollableView = useScrollableView();
  const [ sticked, setSticked ] = React.useState(true);
  const scrollContainer = scrollableView.scrollContainer;
  const [ body, setBody ] = React.useState<HTMLDivElement | null>(null);
  const { data: messages = [], isLoading, isFetchedAfterMount } = useUserMessagesQuery(conversationId);
  const lastMessage = messages[messages.length - 1];

  const [ addToDoModalMessage, setAddToDoModalMessage ] = React.useState<Message | null>(null);

  React.useEffect(() => {
    if (!scrollContainer || !isFetchedAfterMount) return;
    const oldestUnreadMessage = messages.find((message) => !message.seenAt && message.senderId !== me.id);

    if (!oldestUnreadMessage) return;
    scrollContainer.querySelector(`[data-message-id="${oldestUnreadMessage.id}"]`)?.scrollIntoView({ block: 'start' });
  }, [ isFetchedAfterMount ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!scrollContainer || lastMessage?.senderId !== me.id) return;

    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior: 'smooth',
    });
  }, [ lastMessage?.message ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!scrollContainer) return;

    const scrollHandler = () => {
      if (!scrollContainer) return;
      if (scrollContainer.clientHeight === Math.floor(scrollContainer.scrollHeight - scrollContainer.scrollTop)) {
        setSticked(true);
      } else {
        setSticked(false);
      }
    };

    scrollContainer.addEventListener('scroll', scrollHandler);

    return () => scrollContainer.removeEventListener('scroll', scrollHandler);
  }, [ scrollContainer ]);

  React.useEffect(() => {
    if (
      !messagingContext.focusedMessageId
      || !scrollContainer
      || isLoading
    ) {
      return;
    }


    const timeoutId = setTimeout(() => {
      scrollContainer.querySelector(`[data-message-id="${messagingContext.focusedMessageId}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      messagingContext.setFocusedMessageId(undefined);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [ messagingContext.focusedMessageId, isLoading, scrollContainer ]); // eslint-disable-line react-hooks/exhaustive-deps

  useResizeObserver(body, () => {
    if (scrollContainer && sticked) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'instant',
      });
    }
  });

  useResizeObserver(scrollContainer, () => {
    if (scrollContainer && sticked) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'instant',
      });
    }
  });

  const timezone = useTimezone();

  const groups = React.useMemo(() => {
    return messages.reduce((acc, message) => {
      const date = CalendarDateService.date(message.sentAt).tz(timezone).format(DateFormats.ISO_DATE);
      const latestGroup = acc[acc.length - 1];

      if (latestGroup && latestGroup.date === date) {
        latestGroup.messages.push(message);
      } else {
        const newGroup = { date: date, messages: [ message ] };

        acc.push(newGroup);
      }

      return acc;
    }, [] as { date: string; messages: Message[] }[]);
  }, [ messages, timezone ]);

  return (
    <Box
      component="div"
      key={conversationId}
      ref={setBody}
      sx={{
        gap: 0,
        py: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {groups.map(group => (
        <MessagesGroup
          key={group.date}
          color={color}
          group={group}
          setAddToDoModal={setAddToDoModalMessage}
        />
      ))}
      {isLoading && (
        <Loader color={color} />
      )}
      {addToDoModalMessage && (
        <AddToDoModal
          message={addToDoModalMessage}
          onClose={() => setAddToDoModalMessage(null)}
        />
      )}
    </Box>
  );
};
